import { isSameDay, isSameYear, subDays } from 'date-fns';

/**
 * Message times and day starts in the thread.
 *
 * The time is `metadata.createdAt`, in epoch ms. Messages stored before it existed have
 * none and start no day.
 */

type WithMetadata = { metadata?: unknown };

export const readCreatedAt = (message: WithMetadata): number | undefined => {
  const { metadata } = message;
  if (!metadata || typeof metadata !== 'object' || !('createdAt' in metadata)) return undefined;
  const createdAt = (metadata as { createdAt?: unknown }).createdAt;
  return typeof createdAt === 'number' && Number.isFinite(createdAt) ? createdAt : undefined;
};

const setCreatedAt = <T extends WithMetadata>(message: T, createdAt: number): T => ({
  ...message,
  metadata: { ...(message.metadata as object | undefined), createdAt },
});

const withoutCreatedAt = <T extends WithMetadata>(message: T): T => {
  if (readCreatedAt(message) === undefined) return message;
  const { createdAt: _dropped, ...rest } = message.metadata as { createdAt?: number };
  return { ...message, metadata: rest };
};

// Keeps an existing stamp.
export const withCreatedAt = <T extends WithMetadata>(message: T, createdAt: number): T =>
  readCreatedAt(message) === undefined ? setCreatedAt(message, createdAt) : message;

// Max gap between a browser stamp and the server clock. Covers the time from send to save.
export const CLIENT_CLOCK_TOLERANCE_MS = 5 * 60_000;

/**
 * The time the store saves. The server clock decides:
 * - a stored time never changes
 * - an old message without a time stays without one
 * - a new message keeps the browser stamp only if it is close to the server time
 */
export const storedCreatedAt = <T extends WithMetadata>(
  message: T,
  { storedAt, isNew, now }: { storedAt: number | undefined; isNew: boolean; now: number },
): T => {
  if (storedAt !== undefined) return setCreatedAt(message, storedAt);
  if (!isNew) return withoutCreatedAt(message);
  const clientAt = readCreatedAt(message);
  return clientAt !== undefined && Math.abs(clientAt - now) <= CLIENT_CLOCK_TOLERANCE_MS
    ? message
    : setCreatedAt(message, now);
};

/**
 * Messages that start a new day, with their time. Unstamped messages are skipped, so a
 * reply still streaming starts no day. A conversation that starts today gets no line at
 * the top.
 */
export const dayStarts = (messages: readonly (WithMetadata & { id: string })[], today: number) => {
  const starts = new Map<string, number>();
  let previous: number | null = null;
  messages.forEach((message, index) => {
    const createdAt = readCreatedAt(message);
    if (createdAt === undefined) return;
    const newDay = previous === null || !isSameDay(createdAt, previous);
    if (newDay && !(index === 0 && isSameDay(createdAt, today))) starts.set(message.id, createdAt);
    previous = createdAt;
  });
  return starts;
};

export type DayLabel =
  | { kind: 'today' }
  | { kind: 'yesterday' }
  | { kind: 'date'; at: number; withYear: boolean };

// The year shows only for an earlier year.
export const dayLabel = (at: number, now: number): DayLabel => {
  if (isSameDay(at, now)) return { kind: 'today' };
  if (isSameDay(at, subDays(now, 1))) return { kind: 'yesterday' };
  return { kind: 'date', at, withYear: !isSameYear(at, now) };
};
